<script lang="ts" setup>
import { computed, ref, onMounted, onUnmounted, onUpdated } from "vue";
import { pubsub } from "@assets/js/imaios-global/imaios-global";
import { TREE_VIEW_NOTIFICATIONS } from "./notifications.js";
import { useSelectedNodeStore } from "../../treeView-vue3/store/selected-node.store";
import { convertTaxonFromApplication } from "../../treeView-vue3/helpers/taxonomy-api.helper";
import TreeItem from "@templates/components/treeView-vue3/TreeItem.vue";
import TagNomenclature from "@templates/components/tag/TagNomenclature.vue";
import TreeViewSearch from "@templates/components/structures/treeViewSearch/components/treeViewSearch.vue";
import DragBar from "@templates/components/treeView-vue3/DragBar.vue";
import { ArrowSimpleBottom16StrokeIcon } from "@imaios-js/icons/stroke16";
import { ArrowSimpleTop16StrokeIcon } from "@imaios-js/icons/stroke16";
import { CrossIcon } from "@imaios-js/icons/stroke24";
import { Check16StrokeIcon } from "@imaios-js/icons/stroke16";
import { fetchAndSelectItem } from "../../treeView-vue3/helpers";
import { hideAll } from "tippy.js";
import { fetchNewHashByTaId } from "./ta-switch.helper.js";

const APPLICATION = "anatomical";

const props = defineProps<{
  preloadedNodes: Object;
  selectedNodePath?: string;
  currentTaId: string;
  treeKeys: [];
}>();

const totalTreeCount = ref<Number>(Object.keys(props.preloadedNodes).length);
const currentTaId = ref<String>(props.currentTaId);
const currentTreeData = computed(() =>
    convertTaxonFromApplication(
        APPLICATION,
        props.preloadedNodes[`ta-${currentTaId.value}`].flat()
    )
);

// region STORAGE
const store = useSelectedNodeStore();
if (props.selectedNodePath) store.setSelectedNodePath(props.selectedNodePath);
// end region

// region panel width management
const containerTreeView = ref<HTMLElement | null>(null);
const containerWidth = ref<number>(null);
const setWidth = (width) => {
  pubsub.publish(TREE_VIEW_NOTIFICATIONS.DRAGHOLDER_MOUSE_MOVE);
  pubsub.publish(TREE_VIEW_NOTIFICATIONS.DRAGHOLDER_MOUSE_UP);
  containerWidth.value = width + containerTreeView.value.offsetWidth;
};
function getStoredWidth(width: number): void {
  const delta = width - containerTreeView.value.getBoundingClientRect().right;
  containerWidth.value = delta + containerTreeView.value.offsetWidth;
}
const resetContainerWidth = (width) => {
  containerTreeView.value.style.width = width;
  pubsub.publish(TREE_VIEW_NOTIFICATIONS.DRAGHOLDER_MOUSE_MOVE);
  pubsub.publish(TREE_VIEW_NOTIFICATIONS.DRAGHOLDER_MOUSE_UP);
};
//endregion

// region Display responsive
const breakPointMd = window.matchMedia("(max-width: 991.98px)");
const isMobileResponsive = ref<boolean>(false);
const updateMatchesMediaQueries = (matchChangedEvent) => {
  isMobileResponsive.value = !!matchChangedEvent.matches;
  return isMobileResponsive.value;
};
updateMatchesMediaQueries(breakPointMd);
breakPointMd.addEventListener("change", updateMatchesMediaQueries);
// endregion

const isOpenDropdown = ref<Boolean>(false);
const openDropdown = () => {
  if (totalTreeCount.value > 0) isOpenDropdown.value = !isOpenDropdown.value;
};

const closeDropdown = () => {
  isOpenDropdown.value = false;
};

// region HANDLE CHANGE TERMINOLOGIE
const changeTree = (key) => {
  currentTaId.value = key;
  isOpenDropdown.value = false;
};

const findStructure = async (key) => {
  const currentHash = document.getElementById("anatomical-frontpage-content")
      ?.dataset.currentHash;

  // Keep current structure when changing TA
  try {
    if (currentHash) {
      const convertedStructure = await fetchNewHashByTaId(currentHash, key);

      if (convertedStructure && convertedStructure.hash != currentHash)
        await fetchAndSelectItem(
            APPLICATION,
            convertedStructure.url_from,
            true,
            false
        );
    }
  } catch (e) {
    if (e.message !== 'No result found') {
      throw e;
    }
  }
};

const handleChangeTaId = async (key) => {
  changeTree(key);
  await findStructure(key);
}
// endregion

// Hide Tree view in mobile mode if NOT main page
const isMainPage = ref(false);
let observer;
const checkDOM = () => {
  isMainPage.value = !!document.querySelector("#structure-mainpage-title");
};

onMounted(async () => {
  checkDOM(); // initial check
  observer = new MutationObserver(checkDOM); // check if the page is main page or front-page
  observer.observe(document.body, { childList: true, subtree: true });

  // Init navBarTop for the first time landing on page
  if (import.meta.env.DEV) {
    setTimeout(() => updateHeight(), 100);
  } else {
    updateHeight();
  }

  // Hide immediately PreviewTooltip while scrolling
  const scrollContainer = document.getElementById(
      "tree-view-anatomical-content"
  );
  scrollContainer.addEventListener("scroll", () => {
    hideAll({ duration: 0 });
  });
});

const treeViewList = ref<HTMLElement>(null);

onUpdated(() => {
  if (!isMainPage.value) document.body.style.overflow = "";

  const el = treeViewList.value;
  if (!el) return;

  // Dynamic width for item when resize drag bar
  el.addEventListener("scroll", (e) => {
    const scrollHorizontal = e.currentTarget.scrollLeft;

    if (scrollHorizontal) {
      const scrollWidth = el.scrollWidth;
      setWidthForItem(scrollWidth);
    } else {
      setWidthForItem();
    }
  });
});

onUnmounted(() => {
  observer?.disconnect();
});

window.addEventListener("popstate", async () => {
  const taIdFromUrl = document.location.href.match(/#from=(\d+)$/)?.[1];

  if (taIdFromUrl === undefined) {
    // if the url do not have #from=?, force a refresh page to avoid inconsistencies
    location.reload();
    return;
  }

  if (taIdFromUrl != currentTaId.value) {
    // Force refresh taId if historic page is not the same taId with current page
    changeTree(taIdFromUrl);
  }

  await fetchAndSelectItem(APPLICATION, document.location.href, true, true);
});

/* Calcul padding top dynamic */
const windowScroll = ref<number>(0);
const navBarTop = ref<number>(0);
const calcNav = computed(() => {
  return `calc(100svh - ${navBarTop.value + 10}px)`;
});
function updateHeight() {
  navBarTop.value = containerTreeView.value.getBoundingClientRect().top;
}

window.addEventListener("scroll", () => {
  windowScroll.value = window.scrollY;
  if (windowScroll.value <= 200) {
    updateHeight();
  }
});

function setWidthForItem(width) {
  document.querySelectorAll('[role="treeitem"]').forEach((el) => el.style.width = width ? width + 'px' : '');
}
/*--------------*/

window.imaios.pubsub.subscribe(
    window.imaios.pubsub,
    TREE_VIEW_NOTIFICATIONS.CURRENT_TA_CHANGE,
    async (changedTaId) => {
      await changeTree(Number(changedTaId));
    }
);

/* Mobile zone */
const isFocusSearchBar = ref<Boolean>(false);
window.imaios.pubsub.subscribe(
    window.imaios.pubsub,
    TREE_VIEW_NOTIFICATIONS.UPDATE_MOBILE_STRUCTURE,
    (data) => {
      isFocusSearchBar.value = data.active;
    }
);

function focusSearchBarOnMobileMode() {
  if (!isMobileResponsive.value) return;
  document.body.style.overflow = "hidden"; // disable scroll on search in mobile mode
  isFocusSearchBar.value = true;
  containerTreeView.value.classList.add("search-nav-mobile");
}

function closeSearchMobile() {
  document.body.style.overflow = "";
  isFocusSearchBar.value = false;
  containerTreeView.value.classList.remove("search-nav-mobile");
}
/*--------------*/
</script>

<template>
  <nav
      v-show="isMainPage || !isMobileResponsive"
      id="tree-view-anatomical"
      class="tree-view--container"
      :class="{ 'responsive-mobile': isMobileResponsive }"
      ref="containerTreeView"
      :style="{ width: containerWidth + 'px', height: calcNav }"
      :data-tree-taid="currentTaId"
  >
    <div class="tree-view--content-header">
      <div class="widget-title">
        <div
            class="widget-title-container"
            @click="openDropdown"
            v-click-outside="closeDropdown"
        >
          <span class="tree-view-title">
            <template v-if="currentTaId == 4">
              {{ $t(`structures.main.anatomy.veterinary`) }}
            </template>
            <template v-else>
              {{ $t(`structures.main.anatomy.humain${currentTaId}`) }}
            </template>

            <TagNomenclature :id="Number(currentTaId)" />
          </span>

          <template v-if="currentTaId != 4 && totalTreeCount">
            <ArrowSimpleTop16StrokeIcon v-if="isOpenDropdown" />
            <ArrowSimpleBottom16StrokeIcon v-else />
          </template>
        </div>
        <CrossIcon v-if="isFocusSearchBar" @click.prevent="closeSearchMobile" />
      </div>
      <div id="tree-view-listing-ta" v-if="currentTaId != 4 && isOpenDropdown">
        <ul class="dropdown-menu show">
          <li
              class="dropdown-item"
              v-for="key in treeKeys"
              :class="{ active: currentTaId === key }"
              @click.prevent="handleChangeTaId(key)"
              @touchstart.prevent="handleChangeTaId(key)"
          >
            <span>
              {{ $t(`structures.main.anatomy.humain${key}`) }}
              <TagNomenclature :id="key" />
            </span>
            <Check16StrokeIcon v-if="currentTaId == key" />
          </li>
        </ul>
      </div>
      <TreeViewSearch
          style="margin-bottom: 1rem"
          :taId="Number(currentTaId)"
          :is-mobile-mode="isMobileResponsive"
          @onFormFocus="focusSearchBarOnMobileMode"
      />
    </div>

    <div class="tree-view--content">
      <ul ref="treeViewList" id="tree-view-anatomical-content" role="tree" class="tree-view-list">
        <TreeItem
            v-for="data in currentTreeData"
            :data="data"
            :key="data.id"
            :depth="0"
            :expanded-item="false"
            :lineStyle="true"
            :application="APPLICATION"
            :tooltip="!isMobileResponsive"
        />
      </ul>
      <DragBar
          v-if="!isMobileResponsive"
          @init-local-storage="getStoredWidth"
          @update-container-width="setWidth"
          @on-page-resize="resetContainerWidth"
      />
    </div>
  </nav>
</template>

<style scoped lang="scss">
.responsive-mobile {
  position: unset;
  max-width: unset;
  width: unset !important;
  min-width: unset;
  height: unset;
  margin-top: 6.75rem;

  div#tree-view-listing-ta {
    left: 2rem;
  }
}

.responsive-mobile.search-nav-mobile {
  position: fixed;
  z-index: 999;
  top: 0;
  left: 0;
  width: 100% !important;
  height: 100% !important;
  margin-top: unset;

  .tree-view--content {
    display: none;
  }
}

.widget-title {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .widget-title-container {
    display: flex;
    align-items: center;
    gap: 1rem;
    cursor: pointer;
  }
}

.tree-view-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
</style>
